import type { ReactNode } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { MapPin, MessageCircle, Package, User } from "lucide-react";
import { BrandMark } from "~/components/brand-mark";
import { api } from "~/lib/api";
import { cn } from "~/lib/utils";

type Tab = { to: string; label: string; icon: typeof Package; owns: (path: string) => boolean };

const under = (prefix: string) => (path: string) => path === prefix || path.startsWith(`${prefix}/`);

const TABS: Tab[] = [
  {
    to: "/",
    label: "Parcels",
    icon: Package,
    owns: (path) => path === "/" || ["/parcel", "/register", "/history"].some((prefix) => under(prefix)(path)),
  },
  { to: "/addresses", label: "Address", icon: MapPin, owns: under("/addresses") },
  { to: "/account", label: "Account", icon: User, owns: under("/account") },
];

export function CustomerShell({ children }: { children: ReactNode }) {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const unread = useQuery({ queryKey: ["my-unread"], queryFn: () => api.unread(), staleTime: 3000 });
  const newMessages = Object.values(unread.data?.parcel ?? {}).reduce((sum, count) => sum + count, 0);

  return (
    <div className="bg-background text-foreground flex min-h-svh flex-col">
      <a href="#main" className="skip-link">
        Skip to content
      </a>
      <header
        className="border-hairline bg-surface/80 sticky top-0 z-30 border-b backdrop-blur-md"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="mx-auto flex h-14 w-full max-w-3xl items-center justify-between px-4">
          <Link to="/" className="flex h-11 items-center" aria-label="ParcelPoint Ship, your parcels">
            <BrandMark size={24} withWordmark />
          </Link>
          <div className="flex items-center gap-1">
            <nav className="hidden items-center gap-1 md:flex" aria-label="Main">
              {TABS.map((tab) => {
                const on = tab.owns(path);
                return (
                  <Link
                    key={tab.to}
                    to={tab.to}
                    aria-current={on ? "page" : undefined}
                    className={cn(
                      "rounded-md px-3.5 py-2 text-[14px] font-medium transition-colors",
                      on ? "bg-accent text-foreground" : "text-muted-foreground hover:bg-accent/60 hover:text-foreground",
                    )}
                  >
                    {tab.label}
                  </Link>
                );
              })}
            </nav>
            <Link
              to="/messages"
              aria-label={newMessages > 0 ? `Messages, ${newMessages} new` : "Messages"}
              aria-current={path === "/messages" ? "page" : undefined}
              className={cn(
                "relative -mr-2 grid size-11 place-items-center rounded-full transition-colors",
                path === "/messages" ? "bg-accent text-foreground" : "text-muted-foreground hover:bg-accent/60 hover:text-foreground",
              )}
            >
              <MessageCircle className="size-6" strokeWidth={1.6} />
              {newMessages > 0 && (
                <span className="bg-primary text-primary-foreground tabular border-surface absolute top-1 right-0.5 grid h-5 min-w-5 place-items-center rounded-full border-2 px-1 text-[11px] leading-none font-semibold">
                  {newMessages}
                </span>
              )}
            </Link>
          </div>
        </div>
      </header>

      <main id="main" className="mx-auto w-full max-w-3xl flex-1 px-4 py-6 pb-28 md:pb-10">
        {children}
      </main>

      <nav
        aria-label="Main"
        className="border-hairline bg-surface/90 fixed inset-x-0 bottom-0 z-30 border-t backdrop-blur-md md:hidden"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="mx-auto flex max-w-3xl items-stretch">
          {TABS.map((tab) => {
            const on = tab.owns(path);
            const Icon = tab.icon;
            return (
              <Link
                key={tab.to}
                to={tab.to}
                aria-current={on ? "page" : undefined}
                className={cn(
                  "flex min-h-14 flex-1 flex-col items-center justify-center gap-1 text-[11px] font-medium transition-colors",
                  on ? "text-foreground" : "text-muted-foreground",
                )}
              >
                <Icon className="size-6" strokeWidth={on ? 2.1 : 1.6} />
                {tab.label}
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
